import { 
  doc, collection, updateDoc, getDoc, serverTimestamp, arrayUnion, Timestamp,
  runTransaction, query, where, getDocs
} from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';
import { formatRuntimeGameNumber, runtimeEnv } from '@/lib/runtime-env';
import type { Game, GamePlayer, GameState, AppConfig, User } from '@/lib/types';
import NotificationService from './notification.service';

export async function createGame(
  venue: string,
  date: string,
  time: string,
  venueAddress: string,
  adminUserId: string,
  invitedUserIds: string[]
): Promise<string> {
  const userId = adminUserId;

  // Pre-Transaction Query: Authoritative Check
  const activeGamesQuery = query(
    collection(db, 'games'),
    where('status', 'in', ['setup', 'active', 'chip_entry']),
    where('is_deleted', '==', false)
  );
  const activeGamesSnap = await getDocs(activeGamesQuery);
  if (!activeGamesSnap.empty) {
    throw new Error("A game is already in setup or active state. Please settle the current game first.");
  }

  // Resolve user data for invitees (needed for notifications)
  const usersMap: Record<string, { name: string; telegram_username: string | null; telegram_id: number | null; default_buyin_method: 'digital' | 'cash' }> = {};
  if (invitedUserIds.length > 0) {
    for (let i = 0; i < invitedUserIds.length; i += 30) {
      const chunk = invitedUserIds.slice(i, i + 30);
      const snap = await getDocs(query(
        collection(db, 'users'),
        where('__name__', 'in', chunk)
      ));
      snap.forEach(doc => {
        const u = doc.data() as User;
        usersMap[doc.id] = {
          name: u.name || '',
          telegram_username: u.telegram_username || null,
          telegram_id: u.telegram_id || null,
          default_buyin_method: u.default_buyin_method === 'cash' ? 'cash' : 'digital',
        };
      });
    }
  }

  // Atomically increment game number counter
  const counterDocId = runtimeEnv.isNonProduction ? 'game_numbers_dev' : 'game_numbers';
  const counterRef = doc(db, 'counters', counterDocId);
  let gameNumber: number;
  
  try {
    gameNumber = await runTransaction(db, async (tx) => {
      const snap = await tx.get(counterRef);
      const next = snap.exists() ? (snap.data().value || 0) + 1 : 1;
      tx.set(counterRef, { value: next });
      return next;
    });
  } catch (e) {
    // If counter fails, use timestamp as fallback
    gameNumber = Date.now();
  }

  return await runTransaction(db, async (transaction) => {
    // Invariant Check: Atomic Check via Lock Document
    const lockRef = doc(db, 'locks', 'active_game');
    const lockSnap = await transaction.get(lockRef);

    if (lockSnap.exists()) {
      const lockData = lockSnap.data() as { gameId: string };
      if (lockData?.gameId) {
        const activeGameRef = doc(db, 'games', lockData.gameId);
        const activeGameSnap = await transaction.get(activeGameRef);

        if (activeGameSnap.exists()) {
          const activeGame = activeGameSnap.data() as Game;
          if (!activeGame.is_deleted && ['setup', 'active', 'chip_entry'].includes(activeGame.status)) {
            throw new Error("A game is already in setup or active state. Please settle the current game first.");
          }
        }
      }
    }

    // Permission Check: Admin only
    const userRef = doc(db, 'users', userId);
    const userSnap = await transaction.get(userRef);

    if (!userSnap.exists()) throw new Error("User not found");

    const user = userSnap.data() as User;

    if (user.roles?.app_admin !== true) {
      throw new Error("You do not have permission to create a game.");
    }

    const gameData = {
      venue,
      venue_address: venueAddress,
      date,
      time,
      created_at: serverTimestamp(),
      created_by: adminUserId,
      updated_at: serverTimestamp(),
      updated_by: adminUserId,
       start_time: null,
       end_time: null,
       chip_entry_started_at: null,
       archived_at: null,

      status: 'setup' as GameState,
      gameNumber,
      cancelled_at: null,
      venue_lat: null,
      venue_lng: null,
      calendar_event_id: '',
      google_calendar_id: '',
      calendar_last_synced: null,
      calendar_sync_status: 'pending',
      calendar_error_message: null,
      players: invitedUserIds.map(id => ({
        user_id: id,
        name: usersMap[id]?.name || '',
        telegram_username: usersMap[id]?.telegram_username || '',
        telegram_id: usersMap[id]?.telegram_id || null,
        default_buyin_method: usersMap[id]?.default_buyin_method || 'digital',
        photo_url: null,
        rsvp_status: 'no_response',
        arrived: false,
        arrived_at: null,
        arrived_method: null,
        is_spectator: false,
        buy_ins: [],
        total_bought_in: 0,
        cash_in: 0,
         digital_in: 0,
         last_edited_at: null,

        last_edited_by: adminUserId,
        rsvp_google: null,
        rsvp_app: null,
        rsvp_unified: 'pending',
                    rsvp_source: null,
                    rsvp_changed_at: null,
                  })),
       zero_sum_confirmed: false,
       force_settled: false,
       force_settle_discrepancy: null,
       force_settle_reason: null,
       force_settled_by: null,
       is_deleted: false,
       deleted_at: null,
       deleted_by: null,
       reopened_at: null,
       reopened_by: null,
       default_buy_in: 50,

    };

    const newGameRef = doc(collection(db, 'games'));
    transaction.set(newGameRef, gameData);
    transaction.set(doc(db, 'locks', 'active_game'), { 
      gameId: newGameRef.id, 
      updatedAt: serverTimestamp() 
    });
    return newGameRef.id;
  });
}



export async function startGame(gameId: string, adminUserId: string): Promise<void> {
  await runTransaction(db, async (transaction) => {
    const gameRef = doc(db, 'games', gameId);
    const gameSnap = await transaction.get(gameRef);

    if (!gameSnap.exists()) throw new Error('Game not found');

    const userRef = doc(db, 'users', adminUserId);
    const userSnap = await transaction.get(userRef);
    if (!userSnap.exists()) throw new Error('User not found');

    const game = gameSnap.data() as Game;
    const user = userSnap.data() as User;

    // Status Check
    if (game.status !== 'setup') {
      throw new Error('Game is not in setup status and cannot be started.');
    }

    // Permission Check: Admin only
    if (user.roles?.app_admin !== true) {
      throw new Error('You do not have permission to start this game.');
    }

    // Only include players who confirmed (RSVP'd yes)
    const confirmedPlayers = game.players.filter(
      p => p.rsvp_status === 'yes' || p.rsvp_unified === 'confirmed'
    );

    transaction.update(gameRef, {
      status: 'active',
      start_time: serverTimestamp(),
      players: confirmedPlayers,
    });
  });

  // Trigger Telegram Notification
  const gameRef = doc(db, 'games', gameId);
  const gameSnap = await getDoc(gameRef);
  if (gameSnap.exists()) {
    const game = gameSnap.data() as Game;
    
    // Fetch authoritative user data for the notification
    const playersWithData = await Promise.all(
      game.players.map(async (p) => {
        const userRef = doc(db, 'users', p.user_id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data() as User;
            return {
            name: userData.name || p.name,
            telegram_username: userData.telegram_username || p.telegram_username || null,
            telegram_id: userData.telegram_id || p.telegram_id || null
          };
        }
        return {
          name: p.name,
          telegram_username: p.telegram_username || null,
          telegram_id: p.telegram_id || null
        };
      })
    );

    try {
      const configSnap = await getDoc(doc(db, 'config', 'app'));
      const config = configSnap.exists() ? (configSnap.data() as AppConfig) : null;
      const activeChatConfig = config?.telegram_chat_configs?.find(
        c => c.id === config?.telegram_active_chat_id
      );
      const activeChatId = activeChatConfig ? Number(activeChatConfig.chat_id) : undefined;

      if (activeChatId === undefined) {
        return;
      }

      await NotificationService.sendEvent({
        type: 'GAME_STARTED',
        payload: {
          gameId,
          gameNumber: formatRuntimeGameNumber(game.gameNumber),
          venue: game.venue,
          date: game.date,
          time: game.time,
          players: playersWithData,
          chatId: activeChatId,
        },
      });
    } catch (error) {
      console.error('[startGame] Notification queuing failed:', error);
    }
  }
}

export async function updatePlayerRSVP(
  gameId: string,
  playerIndex: number,
  rsvpStatus: 'yes' | 'no' | 'maybe'
): Promise<void> {
  const gameRef = doc(db, 'games', gameId);
  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');
    const game = gameSnap.data() as Game;
    const player = game.players[playerIndex];
    if (!player) throw new Error('Player not found');

    const players = [...game.players];
    const rsvpMap: Record<string, 'confirmed' | 'declined' | 'maybe' | 'pending'> = {
      yes: 'confirmed',
      no: 'declined',
      maybe: 'maybe',
    };

    players[playerIndex] = {
      ...player,
      rsvp_status: rsvpStatus,
      rsvp_app: rsvpStatus,
      rsvp_unified: rsvpMap[rsvpStatus],
      rsvp_source: 'app',
      rsvp_changed_at: Timestamp.now(),
      last_edited_by: auth.currentUser?.uid ?? player.user_id,
    };

    transaction.update(gameRef, {
      players,
      updated_by: auth.currentUser?.uid ?? player.user_id,
      updated_at: serverTimestamp(),
    });
  });
}

export async function addPlayer(
  gameId: string,
  playerData: { user_id: string; name: string; email?: string; default_buyin_method?: 'digital' | 'cash' }
): Promise<void> {
  const gameRef = doc(db, 'games', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');
  const game = gameSnap.data() as Game;

  if (game.players.some(p => p.user_id === playerData.user_id)) {
    throw new Error('Player already in game');
  }

  let defaultBuyInMethod: 'digital' | 'cash' = playerData.default_buyin_method === 'cash' ? 'cash' : 'digital';
  if (!playerData.default_buyin_method && !playerData.user_id.startsWith('guest_')) {
    const userSnap = await getDoc(doc(db, 'users', playerData.user_id));
    if (userSnap.exists()) {
      const userData = userSnap.data() as User;
      defaultBuyInMethod = userData.default_buyin_method === 'cash' ? 'cash' : 'digital';
    }
  }

  const newPlayer: GamePlayer = {
    user_id: playerData.user_id,
    name: playerData.name,
    telegram_username: '',
    telegram_id: null,
    default_buyin_method: defaultBuyInMethod,
    photo_url: null,
    rsvp_status: 'no_response',
    arrived: false,
    arrived_at: null,
    arrived_method: null,
    is_spectator: false,
    buy_ins: [],
    total_bought_in: 0,
    cash_in: 0,
       digital_in: 0,
       last_edited_at: null,

    last_edited_by: auth.currentUser?.uid ?? playerData.user_id,
    left_early: false,
    left_at: null,
    left_stack: 0,
    left_cash: 0,
    rsvp_google: null,
    rsvp_app: null,
    rsvp_unified: 'pending',
         rsvp_source: null,
          rsvp_changed_at: null,
   };

  await updateDoc(gameRef, {
    players: arrayUnion(newPlayer),
    updated_by: auth.currentUser?.uid ?? playerData.user_id,
    updated_at: serverTimestamp(),
  });
}

export async function updateGameSetup(
  gameId: string, 
  updates: { venue: string, date: string, time: string, venue_address: string }
): Promise<void> {
  const gameRef = doc(db, 'games', gameId);
  const gameSnap = await getDoc(gameRef);
  
  if (!gameSnap.exists()) {
    throw new Error('Game not found');
  }
  
  const game = gameSnap.data() as Game;
  
  if (game.status !== 'setup') {
    throw new Error('Only games in setup status can be edited');
  }
  
  await updateDoc(gameRef, {
    ...updates,
    updated_at: serverTimestamp(),
    updated_by: auth.currentUser!.uid,
  });
}


